/**
 * Insights brand lift study: assignment, GAM key-value and exposure
 * @module lib/insights/study
 *
 * The auth host buckets each Insights reader per live study (`h` holdout,
 * `t` test; insightsStudyConfig.js). Ad init fetches the assignment next to
 * the identity lookups and caches it per brand, and every GAM request then
 * carries `{ <keyValue>: h|t }`. The shared ad runtime feeds GPT renders and
 * viewable impressions in here, the Stories player its IMA ads, and the
 * per-study counters decide the reader's survey group:
 *  - holdout + any ad rendered + never rendered the campaign -> control;
 *  - test + at least one viewable campaign impression -> exposed.
 * Ads never wait for a first assignment: the campaign's line items need the
 * key-value (`t`, or `o` for a reader kept out of the study), so a request
 * sent before the bucket is known can't serve it. When the assignment
 * arrives, onStudyAssignment listeners add the bucket to the page's GAM
 * targeting. Ad events seen before then are buffered and applied when it
 * does. Storage failures keep state for this page only, and nothing here
 * throws into ad code.
 */

const {
  STUDY_ASSIGNMENT_PATH,
  STUDY_DEFAULT_TARGETING_KEY,
  STUDY_OUT_OF_STUDY_VALUE,
  STUDY_ASSIGNMENT_STORAGE_PREFIX,
  STUDY_ASSIGNMENT_CACHE_VERSION,
  STUDY_ASSIGNMENT_TTL_MS,
  STUDY_EXPOSURE_STORAGE_PREFIX,
  STUDY_GROUP_WAIT_MS,
  STUDY_MAX_PENDING_EVENTS
} = require('../../insightsStudyConfig');
const { buildMillaAuthUrl } = require('../utils/millaAuth');
const { readStoredAnonId, storeAnonId } = require('./anonSync');

const BUCKETS = ['h', 't'];

// The brand of the last ad init, so targeting builders need no arguments.
let activeBrand = null;
const assignments = new Map(); // brand -> assignment record (this page's copy)
const inflight = new Map(); // brand -> assignment request
const pendingEvents = new Map(); // brand -> ad events awaiting an assignment
const deniedBrands = new Set(); // ads consent DENIED on this page
const exposureFallback = new Map(); // storage key -> counters when storage fails
const waiters = new Set();
const assignmentListeners = new Set(); // called with the brand of each fetched assignment
// slotId -> { matched: [studyKey], viewableCounted } for the slot's last render.
const slotRenders = new Map();

function normalizeBrand(brand) {
  return brand === 'mtlblog' ? 'mtlblog' : 'narcity';
}

function assignmentKey(brand) {
  return `${STUDY_ASSIGNMENT_STORAGE_PREFIX}${brand}`;
}

function exposurePrefix(brand) {
  return `${STUDY_EXPOSURE_STORAGE_PREFIX}${brand}:`;
}

function toIdList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((id) => (id === undefined || id === null ? '' : String(id).trim()))
    .filter(Boolean);
}

function toCount(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
}

// The server's ttlSeconds may shorten the cache life, never extend it.
function toTtlMs(ttlSeconds) {
  const ms = Number(ttlSeconds) * 1000;
  return Number.isFinite(ms) && ms > 0 ? Math.min(ms, STUDY_ASSIGNMENT_TTL_MS) : STUDY_ASSIGNMENT_TTL_MS;
}

function toTime(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function normalizeStudy(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const studyKey = typeof raw.studyKey === 'string' ? raw.studyKey.trim() : '';
  if (!studyKey || !BUCKETS.includes(raw.bucket)) return null;
  return {
    studyKey,
    bucket: raw.bucket,
    keyValue: typeof raw.keyValue === 'string' && raw.keyValue.trim()
      ? raw.keyValue.trim()
      : STUDY_DEFAULT_TARGETING_KEY,
    gamOrderIds: toIdList(raw.gamOrderIds),
    gamLineItemIds: toIdList(raw.gamLineItemIds),
    flightStartsAt: raw.flightStartsAt || null,
    flightEndsAt: raw.flightEndsAt || null
  };
}

// Studies whose flight has not ended. The server only returns live ones; a
// cached entry can outlive its flight.
function liveStudies(record) {
  if (!record) return [];
  const now = Date.now();
  return record.studies.filter((study) => {
    const endsAt = study.flightEndsAt ? Date.parse(study.flightEndsAt) : NaN;
    return !Number.isFinite(endsAt) || now <= endsAt;
  });
}

function readAssignment(brand) {
  if (assignments.has(brand)) return assignments.get(brand);
  try {
    const parsed = JSON.parse(window.localStorage.getItem(assignmentKey(brand)) || 'null');
    if (
      !parsed
      || parsed.v !== STUDY_ASSIGNMENT_CACHE_VERSION
      || !Number.isFinite(parsed.fetchedAt)
      || !Array.isArray(parsed.studies)
    ) {
      return null;
    }
    const record = {
      v: STUDY_ASSIGNMENT_CACHE_VERSION,
      fetchedAt: parsed.fetchedAt,
      ttlMs: toTtlMs(parsed.ttlMs / 1000),
      anonId: typeof parsed.anonId === 'string' ? parsed.anonId : '',
      studies: parsed.studies.map(normalizeStudy).filter(Boolean)
    };
    assignments.set(brand, record);
    return record;
  } catch (_) {
    return null;
  }
}

function writeAssignment(brand, record) {
  assignments.set(brand, record);
  try {
    window.localStorage.setItem(assignmentKey(brand), JSON.stringify(record));
  } catch (_) {
    // this page only
  }
}

// The cached assignment, unless ads consent was denied on this page or the
// Insights id it was made for has since changed (a new id has a new bucket).
function currentAssignment(brand) {
  if (deniedBrands.has(brand)) return null;
  const record = readAssignment(brand);
  if (!record) return null;
  const storedAnon = readStoredAnonId(brand);
  if (storedAnon && record.anonId && storedAnon !== record.anonId) return null;
  return record;
}

function freshExposure(bucket) {
  return {
    bucket,
    assignedAt: Date.now(),
    viewableExposures: 0,
    renderedExposures: 0,
    adsRendered: 0,
    pageviews: 0,
    first: null,
    last: null,
    contaminated: false
  };
}

// The study's counters for this reader. A record kept for the other bucket
// (the Insights id was reset and re-bucketed) starts over.
function readExposure(brand, study) {
  const key = `${exposurePrefix(brand)}${study.studyKey}`;
  let stored = null;
  try {
    stored = JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch (_) {
    stored = null;
  }
  stored = stored || exposureFallback.get(key) || null;
  if (!stored || stored.bucket !== study.bucket) return freshExposure(study.bucket);
  return {
    bucket: study.bucket,
    assignedAt: toTime(stored.assignedAt) || Date.now(),
    viewableExposures: toCount(stored.viewableExposures),
    renderedExposures: toCount(stored.renderedExposures),
    adsRendered: toCount(stored.adsRendered),
    pageviews: toCount(stored.pageviews),
    first: toTime(stored.first),
    last: toTime(stored.last),
    contaminated: stored.contaminated === true
  };
}

function updateExposure(brand, study, mutate) {
  const key = `${exposurePrefix(brand)}${study.studyKey}`;
  const record = readExposure(brand, study);
  mutate(record);
  exposureFallback.set(key, record);
  try {
    window.localStorage.setItem(key, JSON.stringify(record));
  } catch (_) {
    // this page only
  }
}

// A GPT render belongs to the study when GAM served one of its orders
// (`campaignId`) and, when the study lists line items, one of those.
function renderMatchesStudy(study, campaignId, lineItemId) {
  if (campaignId === undefined || campaignId === null) return false;
  if (!study.gamOrderIds.includes(String(campaignId))) return false;
  return study.gamLineItemIds.length === 0 || study.gamLineItemIds.includes(String(lineItemId));
}

// An IMA video ad carries no order id, only VAST ad ids (GAM's `<Ad id>` is
// the line item, wrappers included), so it matches on the listed line items.
function videoAdMatchesStudy(study, adIds) {
  return adIds.some((adId) => study.gamLineItemIds.includes(adId));
}

function applyEvent(brand, record, event) {
  const studies = liveStudies(record);
  if (event.type === 'pageview') {
    studies.forEach((study) => updateExposure(brand, study, (exposure) => {
      exposure.pageviews += 1;
    }));
    return;
  }

  if (event.type === 'render') {
    const matched = [];
    if (!event.isEmpty) {
      studies.forEach((study) => {
        const isMatch = event.adIds
          ? videoAdMatchesStudy(study, event.adIds)
          : renderMatchesStudy(study, event.campaignId, event.lineItemId);
        if (isMatch) matched.push(study.studyKey);
        updateExposure(brand, study, (exposure) => {
          exposure.adsRendered += 1;
          if (!isMatch) return;
          exposure.renderedExposures += 1;
          // The campaign's line items need `t` (or `o`): a holdout render
          // means a line item's targeting is wrong.
          if (study.bucket === 'h') exposure.contaminated = true;
        });
      });
    }
    // Each render (slots refresh up to 10 times) gets its own viewable count.
    if (event.slotId) slotRenders.set(event.slotId, { matched, viewableCounted: false });
    return;
  }

  if (event.type === 'viewable') {
    const render = event.slotId ? slotRenders.get(event.slotId) : null;
    if (!render || render.viewableCounted) return;
    render.viewableCounted = true;
    studies.forEach((study) => {
      if (!render.matched.includes(study.studyKey)) return;
      updateExposure(brand, study, (exposure) => {
        exposure.viewableExposures += 1;
        exposure.first = exposure.first || event.at;
        exposure.last = event.at;
      });
    });
  }
}

function flushPendingEvents(brand, record) {
  const queue = pendingEvents.get(brand);
  if (!queue) return;
  pendingEvents.delete(brand);
  queue.forEach((event) => applyEvent(brand, record, event));
}

/**
 * The survey group the counters give: holdout + an ad rendered + clean ->
 * control; test + a viewable campaign impression -> exposed; else null.
 */
function deriveStudyGroup(bucket, exposure) {
  if (!exposure) return null;
  if (bucket === 'h') {
    return exposure.adsRendered >= 1 && exposure.contaminated !== true ? 'control' : null;
  }
  if (bucket === 't') return exposure.viewableExposures >= 1 ? 'exposed' : null;
  return null;
}

/**
 * `{ studyKey, group: 'exposed'|'control', context }` for the first live
 * study (or `studyKey`) that gives this reader a group, else null.
 */
function resolveStudyGroup(brand, studyKey) {
  try {
    const key = normalizeBrand(brand);
    const studies = liveStudies(currentAssignment(key));
    for (let index = 0; index < studies.length; index += 1) {
      const study = studies[index];
      if (studyKey && study.studyKey !== studyKey) continue;
      const exposure = readExposure(key, study);
      const group = deriveStudyGroup(study.bucket, exposure);
      if (group) {
        return {
          studyKey: study.studyKey,
          group,
          context: {
            viewableExposures: exposure.viewableExposures,
            renderedExposures: exposure.renderedExposures,
            adsRendered: exposure.adsRendered,
            pageviews: exposure.pageviews
          }
        };
      }
    }
  } catch (_) {
    // no group
  }
  return null;
}

// Whether this page's ads can still settle a group: an assignment is on its
// way, or a live study is undecided. A contaminated holdout never decides,
// and a test reader cannot be exposed before the flight starts.
function studyGroupMayResolve(brand) {
  if (deniedBrands.has(brand)) return false;
  const record = currentAssignment(brand);
  if (!record) return inflight.has(brand);
  const now = Date.now();
  return liveStudies(record).some((study) => {
    if (study.bucket === 'h') return !readExposure(brand, study).contaminated;
    const startsAt = study.flightStartsAt ? Date.parse(study.flightStartsAt) : NaN;
    return !Number.isFinite(startsAt) || startsAt <= now;
  });
}

function notifyWaiters(brand) {
  Array.from(waiters).forEach((waiter) => {
    if (waiter.brand !== brand) return;
    try {
      waiter.check();
    } catch (_) {
      // the waiter's timer still settles it
    }
  });
}

function recordEvent(brand, event) {
  try {
    const key = normalizeBrand(brand);
    if (deniedBrands.has(key)) return;
    const record = currentAssignment(key);
    if (!record) {
      const queue = pendingEvents.get(key) || [];
      if (queue.length < STUDY_MAX_PENDING_EVENTS) queue.push(event);
      pendingEvents.set(key, queue);
      return;
    }
    flushPendingEvents(key, record);
    applyEvent(key, record, event);
    notifyWaiters(key);
  } catch (_) {
    // Exposure capture never interrupts ad code.
  }
}

async function requestAssignment(brand) {
  if (typeof fetch !== 'function') return null;
  const anonId = readStoredAnonId(brand);
  // text/plain JSON is a CORS simple request: ad init waits on this, and a
  // preflight would be a second round trip first. Credentials still go.
  const response = await fetch(buildMillaAuthUrl(STUDY_ASSIGNMENT_PATH), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ brand, anonId: anonId || undefined })
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.success !== true || deniedBrands.has(brand)) return null;
  const resolvedAnon = typeof data.anonId === 'string' && data.anonId ? data.anonId : anonId;
  if (resolvedAnon) storeAnonId(brand, resolvedAnon);
  const record = {
    v: STUDY_ASSIGNMENT_CACHE_VERSION,
    fetchedAt: Date.now(),
    ttlMs: toTtlMs(data.ttlSeconds),
    anonId: resolvedAnon || '',
    studies: (Array.isArray(data.studies) ? data.studies : []).map(normalizeStudy).filter(Boolean)
  };
  writeAssignment(brand, record);
  return record;
}

function notifyAssignmentListeners(brand) {
  Array.from(assignmentListeners).forEach((listener) => {
    try {
      listener(brand);
    } catch (_) {
      // one listener never stops the others
    }
  });
}

function refreshAssignment(brand) {
  if (inflight.has(brand)) return inflight.get(brand);
  const request = requestAssignment(brand)
    .catch(() => null)
    .then((record) => {
      inflight.delete(brand);
      try {
        if (record) flushPendingEvents(brand, record);
        notifyWaiters(brand);
        if (record) notifyAssignmentListeners(brand);
      } catch (_) {
        // the stored assignment still applies from the next event
      }
      return record;
    });
  inflight.set(brand, request);
  return request;
}

/**
 * Calls `listener(brand)` each time a fetched assignment is stored (a first
 * bucket, or a background refresh), so ad code can add the bucket to
 * requests it sends from then on. Never for a brand whose ads consent was
 * denied. Returns a function that removes the listener.
 */
function onStudyAssignment(listener) {
  if (typeof listener !== 'function') return () => {};
  assignmentListeners.add(listener);
  return () => assignmentListeners.delete(listener);
}

/**
 * This reader's live study assignments for ad init, never waited for. A
 * cached entry applies at once (refreshed in the background once stale).
 * Without one this resolves null at once and the request runs in the
 * background; its bucket reaches GAM through onStudyAssignment. Never rejects.
 */
function ensureStudyAssignments(brand) {
  try {
    const key = normalizeBrand(brand);
    activeBrand = key;
    if (deniedBrands.has(key)) return Promise.resolve(null);
    const record = currentAssignment(key);
    if (!record) {
      refreshAssignment(key);
      return Promise.resolve(null);
    }
    const age = Date.now() - record.fetchedAt;
    if (age < 0 || age >= record.ttlMs) refreshAssignment(key);
    return Promise.resolve(liveStudies(record));
  } catch (_) {
    return Promise.resolve(null);
  }
}

/**
 * Start the assignment request with the ad script preload (SDK init) when no
 * entry is stored, so ad init's ensureStudyAssignments joins a request already
 * in flight instead of starting it at DOMContentLoaded, and the bucket reaches
 * the earliest possible ad requests. Ad init still applies consent. Never
 * throws.
 */
function prefetchStudyAssignments(brand) {
  try {
    const key = normalizeBrand(brand);
    if (deniedBrands.has(key) || currentAssignment(key)) return;
    refreshAssignment(key);
  } catch (_) {
    // ad init still asks
  }
}

/**
 * Drop the brand's assignment and exposure (ads consent DENIED). The page
 * sends no key-value and records nothing more; a late answer is discarded.
 */
function clearStudyAssignments(brand) {
  const key = normalizeBrand(brand);
  const prefix = exposurePrefix(key);
  try {
    deniedBrands.add(key);
    assignments.delete(key);
    pendingEvents.delete(key);
    Array.from(exposureFallback.keys()).forEach((storageKey) => {
      if (storageKey.startsWith(prefix)) exposureFallback.delete(storageKey);
    });
    notifyWaiters(key);
  } catch (_) {
    // storage cleanup below still runs
  }
  try {
    const storage = window.localStorage;
    const stale = [assignmentKey(key)];
    for (let index = 0; index < storage.length; index += 1) {
      const storageKey = storage.key(index);
      if (storageKey && storageKey.startsWith(prefix)) stale.push(storageKey);
    }
    stale.forEach((storageKey) => storage.removeItem(storageKey));
  } catch (_) {
    // ignore storage failures
  }
}

/**
 * GAM key-values for the last ad init's live studies, e.g. `{ nbls: 'h' }`;
 * studies sharing a key send both values. A reader kept out of the studies
 * (ads consent DENIED) sends `{ nbls: 'o' }`. `{}` while the bucket is
 * unknown: the campaign's line items need `t` or `o`, so those requests
 * can't serve it.
 */
function studyTargeting() {
  const targeting = {};
  try {
    if (!activeBrand) return targeting;
    if (deniedBrands.has(activeBrand)) {
      targeting[STUDY_DEFAULT_TARGETING_KEY] = STUDY_OUT_OF_STUDY_VALUE;
      return targeting;
    }
    liveStudies(currentAssignment(activeBrand)).forEach((study) => {
      const values = [].concat(targeting[study.keyValue] || []);
      if (!values.includes(study.bucket)) values.push(study.bucket);
      targeting[study.keyValue] = values.length === 1 ? values[0] : values;
    });
  } catch (_) {
    return {};
  }
  return targeting;
}

function recordStudyPageview(brand) {
  recordEvent(brand, { type: 'pageview', at: Date.now() });
}

function recordStudySlotRender(brand, render) {
  const details = render || {};
  recordEvent(brand, {
    type: 'render',
    slotId: details.slotId || '',
    campaignId: details.campaignId,
    lineItemId: details.lineItemId,
    isEmpty: details.isEmpty === true,
    at: Date.now()
  });
}

function recordStudySlotViewable(brand, slotId) {
  recordEvent(brand, { type: 'viewable', slotId: slotId || '', at: Date.now() });
}

/**
 * A Stories (IMA) ad from the player hook (storiesAdOptions `onVideoAd`):
 * `started` counts like a GPT render, `viewable` like impressionViewable,
 * once per ad `key`.
 */
function recordStudyVideoAd(brand, detail) {
  const info = detail || {};
  const slotId = `video:${info.key || ''}`;
  if (info.event === 'viewable') {
    recordStudySlotViewable(brand, slotId);
    return;
  }
  if (info.event !== 'started') return;
  recordEvent(brand, {
    type: 'render',
    slotId,
    adIds: toIdList(info.adIds),
    isEmpty: false,
    at: Date.now()
  });
}

/**
 * Resolves with the reader's study group. Waits up to `timeoutMs` only while
 * this page's ads can still settle it (calling `options.onWait(timeoutMs)`
 * first); a reader with no live assignment resolves null at once.
 */
function waitForStudyGroup(brand, timeoutMs = STUDY_GROUP_WAIT_MS, options = {}) {
  try {
    const key = normalizeBrand(brand);
    const known = resolveStudyGroup(key);
    if (known || !studyGroupMayResolve(key)) return Promise.resolve(known);
    if (options && typeof options.onWait === 'function') {
      try {
        options.onWait(timeoutMs);
      } catch (_) {
        // the wait itself is still bounded
      }
    }
    return new Promise((resolve) => {
      let timer = null;
      const waiter = { brand: key, check: null };
      const finish = (group) => {
        waiters.delete(waiter);
        clearTimeout(timer);
        resolve(group);
      };
      waiter.check = () => {
        const group = resolveStudyGroup(key);
        if (group || !studyGroupMayResolve(key)) finish(group);
      };
      timer = setTimeout(() => finish(resolveStudyGroup(key)), timeoutMs);
      waiters.add(waiter);
    });
  } catch (_) {
    return Promise.resolve(null);
  }
}

/**
 * The impression's exposure signal for a study survey, re-read so the latest
 * counts are sent; null once the reader no longer has a group in it.
 */
function studyImpressionSignal(brand, studyKey) {
  if (!studyKey) return null;
  const resolved = resolveStudyGroup(brand, studyKey);
  if (!resolved) return null;
  return {
    exposure: resolved.group === 'exposed',
    exposureCampaignId: resolved.studyKey,
    studyContext: resolved.context
  };
}

module.exports = {
  ensureStudyAssignments,
  prefetchStudyAssignments,
  onStudyAssignment,
  clearStudyAssignments,
  studyTargeting,
  recordStudyPageview,
  recordStudySlotRender,
  recordStudySlotViewable,
  recordStudyVideoAd,
  deriveStudyGroup,
  resolveStudyGroup,
  waitForStudyGroup,
  studyImpressionSignal
};
